/**
 * GlobalView — WebSocket Client (§3.3)
 *
 * Connects to the API WebSocket for real-time entity updates.
 * Auto-reconnects with exponential backoff.
 * Dispatches entity deltas to the entityStore.
 *
 * Handles React StrictMode double-mount gracefully.
 */

import type { WSMessage, EntityLivePayload, Entity } from '@globalview/shared';
import { useEntityStore } from '../stores/entityStore';
import { getIdToken } from './auth';

// ─── Config ──────────────────────────────────────────────────────

// Build WS URL relative to current host (goes through Vite proxy in dev)
function getWsUrl(): string {
  if (import.meta.env.VITE_WS_URL) return import.meta.env.VITE_WS_URL;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

// ─── State ───────────────────────────────────────────────────────

let socket: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let intentionalClose = false;
let connecting = false;

// ─── Convert EntityDelta to full Entity for upsert ───────────────

function deltasToEntities(payload: EntityLivePayload): Entity[] {
  return payload.updates.map((delta) => {
    // Detect entity type from ID prefix
    const isRail = delta.id.startsWith('rail-');
    const isSpace = delta.id.startsWith('space-');
    const entityType = isSpace ? 'satellite' : isRail ? 'train' : 'aircraft';
    const source = isSpace ? 'celestrak' : isRail ? 'sbb' : 'opensky';
    const defaultColor = isSpace ? '#FFD700' : isRail ? '#4a9a2f' : '#00d4ff';
    const symbol = isSpace ? 'satellite' : isRail ? 'train' : 'aircraft';

    return {
      id: delta.id,
      type: entityType as Entity['type'],
      source,
      position: {
        lat: delta.position?.lat ?? 0,
        lon: delta.position?.lon ?? 0,
        alt: delta.position?.alt ?? 0,
      },
      heading: delta.heading,
      timestamp: delta.timestamp || new Date().toISOString(),
      style: {
        color: defaultColor,
        symbol,
        size: 1,
        showTrail: false,
      },
      properties: delta.properties || {},
      confidence: delta.confidence ?? 1.0,
    };
  });
}

// ─── Message Handler ─────────────────────────────────────────────

function handleMessage(event: MessageEvent): void {
  try {
    const msg = JSON.parse(event.data as string) as WSMessage;

    switch (msg.channel) {
      case 'entities:live': {
        const payload = msg.payload as EntityLivePayload;
        const entities = deltasToEntities(payload);
        useEntityStore.getState().upsertEntities(entities);

        if (payload.removed && payload.removed.length > 0) {
          useEntityStore.getState().removeEntities(payload.removed);
        }

        useEntityStore.getState().setSourceStatus('online');
        break;
      }

      case 'system:welcome':
        console.log('[WS] Connected:', msg.payload);
        break;

      default:
        console.debug('[WS] Unknown channel:', msg.channel);
    }
  } catch (err) {
    console.error('[WS] Failed to parse message:', err);
  }
}

// ─── Connection Management ───────────────────────────────────────

function clearReconnectTimer(): void {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function scheduleReconnect(): void {
  if (intentionalClose) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn(`[WS] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached, stopping`);
    useEntityStore.getState().setSourceStatus('offline');
    return;
  }

  clearReconnectTimer();

  const delay = Math.min(
    RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts),
    RECONNECT_MAX_MS,
  );

  console.log(`[WS] Reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);

  reconnectTimer = setTimeout(() => {
    reconnectAttempts++;
    connect();
  }, delay);
}

/** Connect to the WebSocket */
export async function connect(): Promise<void> {
  // Guard: already connected or connecting
  if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
  if (connecting) return;

  connecting = true;
  intentionalClose = false;
  clearReconnectTimer();

  // Get auth token
  let url = getWsUrl();
  try {
    const token = await getIdToken();
    if (token) {
      url += `?token=${encodeURIComponent(token)}`;
    }
  } catch {
    console.warn('[WS] No auth token available, connecting without auth');
  }

  try {
    socket = new WebSocket(url);
  } catch (err) {
    console.error('[WS] Failed to create WebSocket:', err);
    connecting = false;
    scheduleReconnect();
    return;
  }

  socket.onopen = () => {
    console.log('[WS] Connected');
    connecting = false;
    reconnectAttempts = 0;
    useEntityStore.getState().setWsConnected(true);

    // Subscribe to air and rail entities
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          channel: 'entities:subscribe',
          payload: { layers: ['air', 'rail'] },
          ts: new Date().toISOString(),
        }),
      );
    }
  };

  socket.onmessage = handleMessage;

  socket.onclose = () => {
    console.log('[WS] Disconnected');
    connecting = false;
    useEntityStore.getState().setWsConnected(false);
    socket = null;
    scheduleReconnect();
  };

  socket.onerror = () => {
    console.warn('[WS] Connection error (API server may not be running)');
    connecting = false;
    useEntityStore.getState().setSourceStatus('degraded');
  };
}

/** Disconnect from the WebSocket */
export function disconnect(): void {
  intentionalClose = true;
  connecting = false;
  clearReconnectTimer();

  if (socket) {
    // Remove event handlers to avoid triggering reconnect on close
    socket.onclose = null;
    socket.onerror = null;
    socket.onmessage = null;
    socket.onopen = null;
    try {
      socket.close();
    } catch {
      // ignore close errors
    }
    socket = null;
  }
  useEntityStore.getState().setWsConnected(false);
}

/** Check if connected */
export function isConnected(): boolean {
  return socket?.readyState === WebSocket.OPEN;
}

/** Send current camera viewport to server for dynamic polling. */
export function sendViewport(
  viewport: { lamin: number; lomin: number; lamax: number; lomax: number } | null,
): void {
  if (!socket || socket.readyState !== WebSocket.OPEN) return;
  socket.send(
    JSON.stringify({
      channel: 'viewport:update',
      payload: viewport,
      ts: new Date().toISOString(),
    }),
  );
}
